/**
 * EvergreenPage - Übersicht für Evergreen-Inhalte
 * 
 * Zeigt alle Posts die als Evergreen markiert sind mit:
 * - Performance-Status und Warnungen
 * - Intervall und Wiederholungszähler
 * - Aktionen zum Bearbeiten der Einstellungen
 */

import { useState, useEffect, useCallback } from "react";
import { NavLink } from "react-router-dom";
import axios from "axios";
import { toast } from 'sonner';
import {
  RefreshCw, Loader2, Settings, AlertTriangle, CheckCircle,
  Calendar, Clock, TrendingUp, Pause, Play, MoreVertical,
  Instagram, Facebook, Linkedin, Sparkles, ChevronDown,
  BarChart3, ArrowRight, X, Edit3, ChevronUp,
} from "lucide-react";
import { useFeatureGate } from "../hooks/useFeatureGate";
import UpgradeNudge from "../components/UpgradeNudge";
import { ansichtsModell, ANSICHT } from "./analytics/ansichtsModell";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Platform Icon
const PlatformIcon = ({ platform, size = 16 }) => {
  switch (platform?.toLowerCase()) {
    case 'facebook': return <Facebook size={size} className="text-blue-600" />;
    case 'instagram': return <Instagram size={size} className="text-pink-500" />;
    case 'linkedin': return <Linkedin size={size} className="text-blue-700" />;
    case 'tiktok': return <span style={{fontSize: size}} className="font-bold">TT</span>;
    default: return <Calendar size={size} className="text-gray-500" />;
  }
};

// Performance Badge
const PerformanceBadge = ({ status, score }) => {
  const configs = {
    good: { bg: "bg-green-100", text: "text-green-700", label: "Gut" },
    okay: { bg: "bg-blue-100", text: "text-blue-700", label: "OK" },
    warning: { bg: "bg-amber-100", text: "text-amber-700", label: "Niedrig" },
    no_data: { bg: "bg-gray-100", text: "text-gray-500", label: "Keine Daten" }
  };
  const config = configs[status] || configs.no_data;
  
  return (
    <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
      {status === 'warning' && <AlertTriangle size={12} />}
      {status === 'good' && <CheckCircle size={12} />}
      {config.label} {score > 0 && `(${score.toFixed(0)})`}
    </div>
  );
};

// Interval Badge
const IntervalBadge = ({ days }) => {
  const labels = {
    14: "2 Wochen",
    30: "1 Monat",
    60: "2 Monate",
    90: "3 Monate"
  };
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-purple-50 text-purple-700 text-xs font-medium">
      <Clock size={12} />
      {labels[days] || `${days} Tage`}
    </span>
  );
};

// Evergreen Post Card
const EvergreenPostCard = ({ post, onToggle, onUpdateSettings, intervals, maxRepeatsOptions }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [newInterval, setNewInterval] = useState(post.evergreen_interval_days || 30);
  const [newMaxRepeats, setNewMaxRepeats] = useState(post.evergreen_max_repeats || 6);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    await onUpdateSettings(post.id, newInterval, newMaxRepeats);
    setSaving(false);
    setIsEditing(false);
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-md transition-shadow" data-testid={`evergreen-post-${post.id}`}>
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <PlatformIcon platform={post.platform} size={20} />
          <span className="font-medium text-gray-800 capitalize">{post.platform}</span>
        </div>
        <div className="flex items-center gap-2">
          <PerformanceBadge status={post.performance_status} score={post.engagement_score} />
          {post.has_performance_warning && (
            <span className="text-amber-500" title="Niedriger Engagement-Score">
              <AlertTriangle size={16} />
            </span>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="mb-3">
        <p className="text-gray-700 text-sm line-clamp-2">
          {post.hook || post.body?.substring(0, 100) || "Kein Text"}
        </p>
      </div>

      {/* Stats Row */}
      <div className="flex flex-wrap items-center gap-3 mb-3 text-sm text-gray-500">
        <div className="flex items-center gap-1">
          <Calendar size={14} />
          <span>{post.scheduled_date || "—"}</span>
        </div>
        <IntervalBadge days={post.evergreen_interval_days || 30} />
        <div className="flex items-center gap-1">
          <RefreshCw size={14} />
          <span>{post.actual_repeat_count || 0} / {post.evergreen_max_repeats || "∞"}</span>
        </div>
      </div>

      {/* Edit Settings */}
      {isEditing ? (
        <div className="bg-gray-50 rounded-lg p-3 mt-3 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Intervall</label>
              <select
                value={newInterval}
                onChange={(e) => setNewInterval(Number(e.target.value))}
                className="w-full text-sm border border-gray-200 rounded px-2 py-1.5"
              >
                {intervals.map(i => (
                  <option key={i.value} value={i.value}>{i.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Max. Wiederholungen</label>
              <select
                value={newMaxRepeats}
                onChange={(e) => setNewMaxRepeats(Number(e.target.value))}
                className="w-full text-sm border border-gray-200 rounded px-2 py-1.5"
              >
                {maxRepeatsOptions.map(m => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button
              onClick={() => setIsEditing(false)}
              className="px-3 py-1 text-sm text-gray-600 hover:bg-gray-200 rounded"
            >
              Abbrechen
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-3 py-1 text-sm bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : "Speichern"}
            </button>
          </div>
        </div>
      ) : (
        <div className="flex justify-between items-center pt-2 border-t border-gray-100">
          <button
            onClick={() => setIsEditing(true)}
            className="flex items-center gap-1 text-sm text-gray-600 hover:text-purple-600"
          >
            <Settings size={14} />
            Einstellungen
          </button>
          <button
            onClick={() => onToggle(post.id, false)}
            className="flex items-center gap-1 text-sm text-red-500 hover:text-red-600"
          >
            <Pause size={14} />
            Deaktivieren
          </button>
        </div>
      )}
    </div>
  );
};

// ── Refresh-Kandidat-Karte (mit integriertem Review-Flow) ────────────────────
//
// Status-Maschine pro Karte:
//   idle       → "KI-Refresh starten"-Button sichtbar
//   loading    → Spinner, Karte gesperrt
//   reviewing  → Inline-Panel: Original | KI-Entwurf | Begründung | 3 CTAs
//   editing    → Entwurf in bearbeitbarer Textarea
//   accepted   → grüne Bestätigungszeile; kein erneutes Starten möglich
//   error      → Fehlerhinweis, erneuter Versuch möglich
//
// Akzeptierter Entwurf → POST /api/content-tools/refresh/{post_id}/accept
// Kein Autopublish. Kein Überschreiben des Originalposts.

const RefreshCandidateCard = ({
  candidate,
  evergreenGate,
  onUnmark,
  onRefreshStart,
  onAcceptDraft,
  onDiscardDraft,
  refreshState,
}) => {
  const status = refreshState?.status || "idle";
  const data   = refreshState?.data;
  const error  = refreshState?.error;

  const [editedText, setEditedText]       = useState("");
  const [isEditing, setIsEditing]         = useState(false);
  const [showRationale, setShowRationale] = useState(false);

  // Wenn Entwurf eintrifft → Textarea initialisieren
  useEffect(() => {
    if (data?.draft) {
      setEditedText(data.draft);
      setIsEditing(false);
      setShowRationale(false);
    }
  }, [data?.draft]);

  const isReviewing = status === "reviewing";
  const isAccepted  = status === "accepted";

  return (
    <div
      className={`rounded-xl border overflow-hidden transition-shadow hover:shadow-sm ${
        isAccepted ? "border-green-200 bg-green-50" : "border-amber-200 bg-white"
      }`}
    >
      {/* ── Karten-Header (immer sichtbar) ──────────────────────────── */}
      <div className="p-4">
        <div className="flex items-start gap-3">
          {/* Post-Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <PlatformIcon platform={candidate.platform} size={14} />
              <span className="text-xs text-stone-400">
                {candidate.date || candidate.posted_at || "—"}
              </span>
              <span className="text-xs px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded-full font-semibold">
                Score {candidate.engagement_score?.toFixed(0) ?? "0"}
              </span>
            </div>
            <p className="text-sm text-stone-700 leading-snug line-clamp-2 mb-1.5">
              {candidate.hook || candidate.theme ||
                `Post #${String(candidate.post_id || "").slice(-4)}`}
            </p>
            <div className="flex items-center gap-3 text-xs text-stone-400">
              <span>👁 {candidate.impressions?.toLocaleString("de-DE") || 0}</span>
              <span>❤️ {candidate.likes || 0}</span>
            </div>
          </div>

          {/* Aktions-Bereich */}
          <div className="flex flex-col gap-1.5 flex-shrink-0 items-end">
            {/* Idle: Refresh starten */}
            {status === "idle" && evergreenGate.allowed && (
              <button
                onClick={() => onRefreshStart(candidate.post_id)}
                className="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                <Sparkles size={12} />
                KI-Refresh
              </button>
            )}

            {/* Idle Fehler: erneut versuchen */}
            {status === "error" && (
              <button
                onClick={() => onRefreshStart(candidate.post_id)}
                className="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 bg-red-50 text-red-600 border border-red-200 rounded-lg hover:bg-red-100 transition-colors"
              >
                <RefreshCw size={12} />
                Erneut versuchen
              </button>
            )}

            {/* Lädt */}
            {status === "loading" && (
              <div className="flex items-center gap-1.5 text-xs text-stone-400 px-2 py-1.5">
                <Loader2 size={13} className="animate-spin" />
                Wird erstellt…
              </div>
            )}

            {/* Reviewing: Panel-Toggle */}
            {isReviewing && (
              <button
                onClick={() => setShowRationale(r => !r)}
                className="flex items-center gap-1 text-xs text-stone-400 hover:text-stone-600"
              >
                {showRationale ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                Begründung
              </button>
            )}

            {/* Akzeptiert */}
            {isAccepted && (
              <span className="flex items-center gap-1 text-xs text-green-700 font-medium">
                <CheckCircle size={12} />
                Gespeichert
              </span>
            )}

            {/* Gate: nicht berechtigt */}
            {!evergreenGate.allowed && status === "idle" && (
              <NavLink
                to={evergreenGate.pricingHref}
                className="flex items-center gap-1 text-xs font-medium px-3 py-1.5 bg-amber-50 text-amber-700 border border-amber-300 rounded-lg hover:bg-amber-100 transition-colors"
              >
                <Sparkles size={12} />
                Ab Creator
              </NavLink>
            )}

            {/* Immer: Vormerkung entfernen */}
            {!isAccepted && (
              <button
                onClick={() => onUnmark(candidate.post_id)}
                className="flex items-center gap-1 text-xs text-stone-400 hover:text-red-500 transition-colors"
                title="Vormerkung aufheben"
              >
                <X size={11} />
                Entfernen
              </button>
            )}
          </div>
        </div>

        {/* Fehlertext */}
        {status === "error" && error && (
          <p className="mt-2 text-xs text-red-500 bg-red-50 px-3 py-1.5 rounded-lg">
            {error}
          </p>
        )}
      </div>

      {/* ── Review-Panel (nur in reviewing/editing) ─────────────────── */}
      {isReviewing && data && (
        <div className="border-t border-amber-100 bg-stone-50 p-4 space-y-4">
          {/* Header */}
          <div className="flex items-center gap-2">
            <Sparkles size={13} className="text-violet-500" />
            <span className="text-xs font-semibold text-stone-600 uppercase tracking-wide">
              KI-Vorschlag zur Überarbeitung
            </span>
            {data.brand_context_used && (
              <span className="ml-auto text-xs text-stone-400 italic">
                Markenprofil berücksichtigt
              </span>
            )}
          </div>

          {/* Begründung (collapsible) */}
          {showRationale && (
            <div className="bg-violet-50 border border-violet-100 rounded-lg p-3">
              <p className="text-xs font-medium text-violet-700 mb-1">
                Warum dieser Vorschlag?
              </p>
              <p className="text-xs text-violet-600 leading-relaxed mb-2">
                {data.rationale}
              </p>
              {data.improvements?.length > 0 && (
                <ul className="space-y-0.5">
                  {data.improvements.map((imp, i) => (
                    <li key={i} className="flex items-start gap-1.5 text-xs text-violet-500">
                      <span className="flex-shrink-0 font-bold">→</span>
                      {imp}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {/* Original vs. Entwurf */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <p className="text-xs font-medium text-stone-400 mb-1.5">Original</p>
              <div className="bg-white rounded-lg border border-stone-200 p-3 text-sm text-stone-400 leading-relaxed whitespace-pre-wrap">
                {data.original_text}
              </div>
            </div>
            <div>
              <p className="text-xs font-medium text-green-600 mb-1.5">
                KI-Entwurf
                {isEditing && (
                  <span className="ml-1 text-stone-400 font-normal">(bearbeite direkt)</span>
                )}
              </p>
              {isEditing ? (
                <textarea
                  value={editedText}
                  onChange={e => setEditedText(e.target.value)}
                  rows={6}
                  className="w-full bg-white rounded-lg border border-green-300 p-3 text-sm text-stone-700 leading-relaxed resize-none focus:outline-none focus:ring-2 focus:ring-green-300"
                />
              ) : (
                <div className="bg-green-50 rounded-lg border border-green-200 p-3 text-sm text-stone-700 leading-relaxed whitespace-pre-wrap">
                  {editedText}
                </div>
              )}
            </div>
          </div>

          {/* Aktions-Buttons */}
          <div className="flex items-center gap-2 flex-wrap pt-1">
            <button
              onClick={() => onAcceptDraft(candidate.post_id, editedText, data)}
              className="flex items-center gap-1.5 px-4 py-2 bg-green-600 text-white text-xs font-semibold rounded-lg hover:bg-green-700 transition-colors"
            >
              <CheckCircle size={13} />
              Entwurf übernehmen
            </button>
            <button
              onClick={() => setIsEditing(e => !e)}
              className="flex items-center gap-1.5 px-3 py-2 bg-white border border-stone-300 text-stone-600 text-xs font-medium rounded-lg hover:border-stone-400 transition-colors"
            >
              <Edit3 size={13} />
              {isEditing ? "Vorschau" : "Bearbeiten"}
            </button>
            <button
              onClick={() => onDiscardDraft(candidate.post_id)}
              className="ml-auto text-xs text-stone-400 hover:text-red-500 transition-colors"
            >
              Verwerfen
            </button>
          </div>
        </div>
      )}

      {/* ── Bestätigungsband (accepted) ──────────────────────────────── */}
      {isAccepted && (
        <div className="border-t border-green-200 bg-green-100 px-4 py-2.5 flex items-center gap-2 flex-wrap">
          <CheckCircle size={13} className="text-green-600 flex-shrink-0" />
          <p className="text-xs text-green-700 font-medium flex-1">
            Entwurf gespeichert – bereit zur weiteren Bearbeitung oder Einplanung.
          </p>
          <NavLink
            to="/content-tools"
            className="flex items-center gap-1 text-xs font-semibold text-green-800 hover:text-green-900 underline underline-offset-2 flex-shrink-0 transition-colors"
          >
            In Content Tools öffnen
            <ArrowRight size={11} />
          </NavLink>
        </div>
      )}
    </div>
  );
};

// Main Component
const EvergreenPage = () => {
  // Feature-Gate: Evergreen ist ab Creator / Pro verfügbar
  const evergreenGate = useFeatureGate("evergreen");

  const [loading, setLoading] = useState(true);
  // Fund 24.08.2026 (dieselbe Lehre wie Auswertung/Dashboard, Runde 11/21): ein
  // fehlgeschlagener Abruf ließ posts=[] stehen, und die Seite zeigte dann "Noch
  // keine Evergreen-Posts" — ununterscheidbar von einem Kunden, der wirklich noch
  // keinen markiert hat. error trennt beide Fälle.
  const [error, setError] = useState(false);
  const [posts, setPosts] = useState([]);
  const [stats, setStats] = useState(null);
  const [intervals, setIntervals] = useState([]);
  const [maxRepeatsOptions, setMaxRepeatsOptions] = useState([]);
  const [runningJob, setRunningJob] = useState(false);
  const [refreshCandidates, setRefreshCandidates] = useState([]);
  const [refreshStates, setRefreshStates] = useState({});

  // Load data
  const loadData = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const [postsRes, statusRes, intervalsRes, candidatesRes] = await Promise.all([
        axios.get(`${API}/evergreen/posts`),
        axios.get(`${API}/evergreen/status`),
        axios.get(`${API}/evergreen/intervals`),
        axios.get(`${API}/analytics/refresh-candidates`).catch(() => null),
      ]);

      setPosts(postsRes.data.posts || []);
      setStats(statusRes.data);
      setIntervals(intervalsRes.data.intervals || []);
      setMaxRepeatsOptions(intervalsRes.data.max_repeats_options || []);
      if (candidatesRes?.data) setRefreshCandidates(candidatesRes.data.candidates || []);
    } catch (error) {
      setError(true);
      toast.error('Fehler beim Laden der Evergreen-Daten');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Toggle evergreen
  const handleToggle = async (postId, isEvergreen) => {
    try {
      await axios.put(`${API}/evergreen/${postId}/toggle`, { is_evergreen: isEvergreen });
      toast.success(isEvergreen ? 'Evergreen aktiviert' : 'Evergreen deaktiviert');
      loadData();
    } catch (error) {
      toast.error('Fehler beim Ändern des Status');
    }
  };

  // Update settings
  const handleUpdateSettings = async (postId, intervalDays, maxRepeats) => {
    try {
      await axios.put(`${API}/evergreen/${postId}/settings`, {
        interval_days: intervalDays,
        max_repeats: maxRepeats
      });
      toast.success('Einstellungen gespeichert');
      loadData();
    } catch (error) {
      toast.error('Fehler beim Speichern');
    }
  };

  // Run recycling job manually
  const handleRunNow = async () => {
    setRunningJob(true);
    try {
      const res = await axios.post(`${API}/evergreen/run-now`);
      const recycledCount = res.data.recycled?.length || 0;
      toast.success(`Recycling abgeschlossen: ${recycledCount} Posts erstellt`);
      loadData();
    } catch (error) {
      toast.error('Fehler beim Ausführen des Recyclings');
    } finally {
      setRunningJob(false);
    }
  };

  // Refresh-Kandidaten-Vormerkung aufheben
  const handleUnmarkCandidate = async (postId) => {
    try {
      await axios.delete(`${API}/analytics/refresh-candidates/${postId}`);
      setRefreshCandidates(prev => prev.filter(c => c.post_id !== postId));
      toast.success('Vormerkung aufgehoben');
    } catch {
      toast.error('Fehler beim Aufheben der Vormerkung');
    }
  };

  // KI-Refresh starten
  const handleRefreshStart = async (postId) => {
    setRefreshStates(prev => ({ ...prev, [postId]: { status: 'loading', data: null, error: null } }));
    try {
      const res = await axios.post(`${API}/content-tools/refresh/${postId}`);
      setRefreshStates(prev => ({ ...prev, [postId]: { status: 'reviewing', data: res.data, error: null } }));
    } catch (err) {
      const msg = err.response?.data?.detail || "Fehler beim Erstellen des KI-Entwurfs";
      setRefreshStates(prev => ({ ...prev, [postId]: { status: 'error', data: null, error: msg } }));
      toast.error(msg);
    }
  };

  // Entwurf akzeptieren und speichern
  const handleAcceptDraft = async (postId, editedText, data) => {
    try {
      await axios.post(`${API}/content-tools/refresh/${postId}/accept`, {
        original_text: data.original_text,
        draft_text: editedText,
        rationale: data.rationale,
        platform: data.platform,
      });
      setRefreshStates(prev => ({ ...prev, [postId]: { status: 'accepted', data, error: null } }));
      toast.success("Entwurf gespeichert.");
    } catch {
      toast.error("Fehler beim Speichern des Entwurfs");
    }
  };

  // Entwurf verwerfen → zurück zu idle
  const handleDiscardDraft = (postId) => {
    setRefreshStates(prev => ({ ...prev, [postId]: { status: 'idle', data: null, error: null } }));
  };

  // Posts with warnings
  const warningPosts = posts.filter(p => p.has_performance_warning);

  // Dieselbe Vier-Zustände-Regel wie die Auswertung (Runde 11): laden/fehler/leer/
  // daten dürfen nie verwechselt werden, sonst sieht ein fehlgeschlagener Abruf wie
  // ein leeres Konto aus.
  const ansicht = ansichtsModell({ laden: loading, ladefehler: error, hatDaten: posts.length > 0 });

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6" data-testid="evergreen-page">
      {/* Header */}
      <div className="max-w-6xl mx-auto mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
              <RefreshCw className="text-green-500" />
              Evergreen-Inhalte
            </h1>
            <p className="text-gray-500 mt-1">Zeitlose Posts die automatisch recycelt werden</p>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={loadData}
              disabled={loading}
              className="p-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50"
              title="Aktualisieren"
            >
              <RefreshCw size={18} className={`text-gray-600 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={handleRunNow}
              disabled={runningJob || posts.length === 0}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
              data-testid="run-recycling-btn"
            >
              {runningJob ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Läuft...
                </>
              ) : (
                <>
                  <Play size={16} />
                  Jetzt recyceln
                </>
              )}
            </button>
          </div>
        </div>

        {/* Upgrade-Nudge: nur wenn Evergreen für aktuellen Plan nicht freigeschaltet */}
        {!evergreenGate.allowed && (
          <UpgradeNudge
            {...evergreenGate}
            featureLabel="Evergreen-Planung"
            className="mt-4"
          />
        )}

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-6">
            <div className="bg-white rounded-lg p-4 border border-gray-100">
              <p className="text-2xl font-bold text-green-600">{stats.total_evergreen_posts}</p>
              <p className="text-sm text-gray-500">Evergreen-Posts</p>
            </div>
            <div className="bg-white rounded-lg p-4 border border-gray-100">
              <p className="text-2xl font-bold text-blue-600">{stats.total_recycled_posts}</p>
              <p className="text-sm text-gray-500">Recycelte Posts</p>
            </div>
            <div className="bg-white rounded-lg p-4 border border-gray-100">
              <p className="text-2xl font-bold text-amber-600">{warningPosts.length}</p>
              <p className="text-sm text-gray-500">Mit Warnung</p>
            </div>
            <div className="bg-white rounded-lg p-4 border border-gray-100">
              <p className="text-sm text-gray-600">{stats.last_job_run ? new Date(stats.last_job_run).toLocaleDateString('de-DE') : '—'}</p>
              <p className="text-sm text-gray-500">Letzter Job</p>
            </div>
          </div>
        )}
      </div>

      {/* ── Refresh-Kandidaten (aus Analytics vorgemerkt) ────────────── */}
      {!loading && refreshCandidates.length > 0 && (
        <div className="max-w-6xl mx-auto mb-6" data-testid="refresh-candidates">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-base font-semibold text-stone-800 flex items-center gap-2">
              <BarChart3 size={18} className="text-amber-500" />
              Inhalte zur Überarbeitung
              <span className="text-sm font-normal text-stone-400">({refreshCandidates.length})</span>
            </h2>
            <NavLink
              to="/analytics"
              className="text-xs text-stone-400 hover:text-fox-fire transition-colors"
            >
              In Analytics ansehen →
            </NavLink>
          </div>
          <p className="text-sm text-stone-500 mb-3">
            Aus Analytics vorgemerkte Posts mit unterdurchschnittlichem Engagement.
            {!evergreenGate.allowed && (
              <span className="text-amber-600 ml-1">Refresh-Funktion ab Wachstum-Plan verfügbar.</span>
            )}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {refreshCandidates.map(c => (
              <RefreshCandidateCard
                key={c.post_id}
                candidate={c}
                evergreenGate={evergreenGate}
                onUnmark={handleUnmarkCandidate}
                onRefreshStart={handleRefreshStart}
                onAcceptDraft={handleAcceptDraft}
                onDiscardDraft={handleDiscardDraft}
                refreshState={refreshStates[c.post_id]}
              />
            ))}
          </div>
        </div>
      )}

      {/* Content */}
      {ansicht === ANSICHT.LADEN ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 size={32} className="animate-spin text-green-500" />
        </div>
      ) : ansicht === ANSICHT.FEHLER ? (
        <div className="max-w-6xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-xl p-8 text-center">
            <p className="text-red-700 mb-4">
              Die Evergreen-Daten konnten nicht geladen werden. Das liegt an uns, nicht
              an deinen Beiträgen — deine Daten sind unverändert.
            </p>
            <button
              onClick={loadData}
              className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700"
            >
              Erneut laden
            </button>
          </div>
        </div>
      ) : ansicht === ANSICHT.LEER ? (
        <div className="max-w-6xl mx-auto">
          <div className="bg-white rounded-xl p-8 text-center border border-gray-100">
            <RefreshCw size={48} className="mx-auto text-gray-300 mb-4" />
            <h2 className="text-lg font-semibold text-gray-700 mb-2">Noch keine Evergreen-Posts</h2>
            <p className="text-gray-500 max-w-md mx-auto">
              Markiere Posts im Monatsplan als Evergreen, um sie automatisch wiederzuverwenden.
              Klicke auf das ♻️ Icon oder öffne die Post-Details.
            </p>
          </div>
        </div>
      ) : (
        <div className="max-w-6xl mx-auto">
          {/* Warning Section */}
          {warningPosts.length > 0 && (
            <div className="mb-6 bg-amber-50 rounded-xl p-4 border border-amber-200">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle size={20} className="text-amber-600" />
                <h3 className="font-semibold text-amber-800">{warningPosts.length} Post(s) mit niedrigem Score</h3>
              </div>
              <p className="text-sm text-amber-700">
                Diese Posts haben einen Engagement-Score unter 50. Überprüfe, ob sie weiterhin recycelt werden sollen.
              </p>
            </div>
          )}

          {/* Posts Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {posts.map(post => (
              <EvergreenPostCard
                key={post.id}
                post={post}
                onToggle={handleToggle}
                onUpdateSettings={handleUpdateSettings}
                intervals={intervals}
                maxRepeatsOptions={maxRepeatsOptions}
              />
            ))}
          </div>
        </div>
      )}

      {/* Info Footer */}
      <div className="max-w-6xl mx-auto mt-8 text-center text-sm text-gray-400">
        <p>Automatisches Recycling läuft täglich um 03:00 Uhr (Europe/Berlin)</p>
      </div>
    </div>
  );
};

export default EvergreenPage;
